import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { FaStar, FaCheckCircle, FaTimes, FaArrowRight } from 'react-icons/fa';

const Comparison: React.FC = () => {
  const [sortBy, setSortBy] = useState<'rating' | 'bonus'>('rating');

  const offers = [
    {
      id: 'bwin',
      name: 'Bwin',
      bonus: '100€',
      image: '/images/offers/bwin.webp',
      rating: 4.8,
      minDeposit: '10€',
      withdrawalTime: '24-48h',
      license: 'ANJ',
      features: {
        mobile: true,
        live: true,
        cashback: true,
        vip: false,
      },
    },
    {
      id: 'unibet',
      name: 'Unibet',
      bonus: '150€',
      image: '/images/offers/unibet.webp',
      rating: 4.9,
      minDeposit: '10€',
      withdrawalTime: '12-24h',
      license: 'ANJ',
      features: {
        mobile: true,
        live: true,
        cashback: true,
        vip: true,
      },
    },
    {
      id: 'vbet',
      name: 'Vbet',
      bonus: '120€',
      image: '/images/offers/vbet.webp',
      rating: 4.7,
      minDeposit: '10€',
      withdrawalTime: '24-48h',
      license: 'ANJ',
      features: {
        mobile: true,
        live: true,
        cashback: false,
        vip: true,
      },
    },
  ];

  const sortedOffers = [...offers].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    return parseInt(b.bonus) - parseInt(a.bonus);
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Comparaison des meilleurs bookmakers français',
    description: 'Comparez les meilleures offres de paris sportifs en France',
    itemListElement: offers.map((offer, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Organization',
        name: offer.name,
        rating: {
          '@type': 'Rating',
          ratingValue: offer.rating,
          bestRating: 5,
        },
      },
    })),
  };

  return (
    <>
      <SEO
        title="Comparaison des Meilleurs Bookmakers Français 2025"
        description="Comparez en détail les meilleures offres de paris sportifs en France. Bonus, cotes, fonctionnalités et avis experts."
        jsonLd={jsonLd}
      />

      <div className="min-h-screen pt-28 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Comparaison des <span className="text-gradient">Bookmakers</span>
          </h1>
          <p className="text-gray-400 text-center mb-8 max-w-2xl mx-auto">
            Comparez facilement les meilleures offres disponibles sur le marché français
          </p>

          {/* Sort Options */}
          <div className="flex justify-center mb-8">
            <div className="glass p-2 rounded-lg flex space-x-2">
              <button
                onClick={() => setSortBy('rating')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  sortBy === 'rating'
                    ? 'bg-accent-primary text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Par note
              </button>
              <button
                onClick={() => setSortBy('bonus')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  sortBy === 'bonus'
                    ? 'bg-accent-primary text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Par bonus
              </button>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-dark-card">
                  <tr>
                    <th className="text-left p-6 text-white font-semibold">Bookmaker</th>
                    <th className="text-center p-6 text-white font-semibold">Note</th>
                    <th className="text-center p-6 text-white font-semibold">Bonus</th>
                    <th className="text-center p-6 text-white font-semibold">Dépôt min.</th>
                    <th className="text-center p-6 text-white font-semibold">Retrait</th>
                    <th className="text-center p-6 text-white font-semibold">Mobile</th>
                    <th className="text-center p-6 text-white font-semibold">Live</th>
                    <th className="text-center p-6 text-white font-semibold">VIP</th>
                    <th className="text-center p-6 text-white font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedOffers.map((offer, index) => (
                    <tr
                      key={offer.id}
                      className={`border-t border-dark-border hover:bg-dark-hover transition-colors ${
                        index % 2 === 0 ? 'bg-dark-surface/50' : ''
                      }`}
                    >
                      <td className="p-6">
                        <div className="flex items-center space-x-4">
                          <img
                            src={offer.image}
                            alt={offer.name}
                            className="w-16 h-16 object-contain"
                            loading="lazy"
                            width="64"
                            height="64"
                          />
                          <div>
                            <div className="text-white font-bold text-lg">{offer.name}</div>
                            <div className="text-gray-400 text-sm">{offer.license}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-6 text-center">
                        <div className="flex items-center justify-center space-x-1">
                          <FaStar className="text-yellow-400" size={16} />
                          <span className="text-white font-semibold">{offer.rating}</span>
                        </div>
                      </td>
                      <td className="p-6 text-center">
                        <span className="bg-accent-primary/20 text-accent-primary px-3 py-1 rounded-full font-semibold">
                          {offer.bonus}
                        </span>
                      </td>
                      <td className="p-6 text-center text-gray-300">{offer.minDeposit}</td>
                      <td className="p-6 text-center text-gray-300">{offer.withdrawalTime}</td>
                      <td className="p-6 text-center">
                        {offer.features.mobile ? (
                          <FaCheckCircle className="text-accent-success mx-auto" size={20} />
                        ) : (
                          <FaTimes className="text-red-400 mx-auto" size={20} />
                        )}
                      </td>
                      <td className="p-6 text-center">
                        {offer.features.live ? (
                          <FaCheckCircle className="text-accent-success mx-auto" size={20} />
                        ) : (
                          <FaTimes className="text-red-400 mx-auto" size={20} />
                        )}
                      </td>
                      <td className="p-6 text-center">
                        {offer.features.vip ? (
                          <FaCheckCircle className="text-accent-success mx-auto" size={20} />
                        ) : (
                          <FaTimes className="text-red-400 mx-auto" size={20} />
                        )}
                      </td>
                      <td className="p-6 text-center">
                        <Link
                          to={`/offre/${offer.id}`}
                          className="inline-flex items-center space-x-2 bg-accent-primary hover:bg-accent-primary/90 text-white font-semibold py-2 px-4 rounded-lg transition-all text-sm"
                        >
                          <span>Voir</span>
                          <FaArrowRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="glass p-6 rounded-xl text-center">
              <div className="text-3xl font-bold text-accent-primary mb-2">
                {offers.length}
              </div>
              <div className="text-gray-400">Bookmakers comparés</div>
            </div>
            <div className="glass p-6 rounded-xl text-center">
              <div className="text-3xl font-bold text-accent-secondary mb-2">
                {Math.max(...offers.map((o) => o.rating)).toFixed(1)}
              </div>
              <div className="text-gray-400">Meilleure note</div>
            </div>
            <div className="glass p-6 rounded-xl text-center">
              <div className="text-3xl font-bold text-accent-success mb-2">
                {Math.max(...offers.map((o) => parseInt(o.bonus)))}€
              </div>
              <div className="text-gray-400">Meilleur bonus</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Comparison;

